/**
 * TrackerPage — bills checklist + variable spending log.
 *
 * Bills section: tap to mark paid, tap again to un-pay. Sorted by due date.
 * Variable spending: log purchases by category, track against budget.
 *
 * Month navigator lets you browse history for both sections.
 */
import React, { useState } from 'react';
import {
  mk,
  isNowMo,
  moIncome,
  moVar,
  moSavings,
  getAllBills,
  getVarBudget,
  getAllCats,
  MOS,
  sourcesToMonthly,
} from '../utils';
import { FixedBills } from './paycheck/FixedBills';
import { VariableSpending } from './paycheck/VariableSpending';

export default function TrackerPage({ state, updateState, showToast, triggerConfetti }) {
  const today = new Date();
  const [viewY, setViewY] = useState(today.getFullYear());
  const [viewM, setViewM] = useState(today.getMonth());

  const {
    cards = [], fixedBills = [], subscriptions = [], paidBills = {},
    varSpend = {}, savDeposits = {}, savContrib = 0, budgetSlider = 50,
    catBudgets = {}, customCats = [], disabledCats = [], incomeLog = [],
    monthlyIncome = 0, incomeSources = [],
  } = state;

  const k = mk(viewY, viewM);
  const cats = getAllCats(customCats, disabledCats);
  const bills = getAllBills(cards, fixedBills, subscriptions);
  const pd = paidBills[k] || {};
  const vd = varSpend[k] || {};

  const prevMo = () => { if (viewM === 0) { setViewY((y) => y - 1); setViewM(11); } else setViewM((m) => m - 1); };
  const nextMo = () => { if (viewM === 11) { setViewY((y) => y + 1); setViewM(0); } else setViewM((m) => m + 1); };

  const srcTotalMonthly = sourcesToMonthly(incomeSources);
  const moLoggedInc = moIncome(incomeLog, k);
  const isFutureMo = viewY > today.getFullYear() || (viewY === today.getFullYear() && viewM > today.getMonth());
  const plannedInc = incomeSources.length > 0 ? srcTotalMonthly : isFutureMo ? 0 : monthlyIncome || 0;
  const inc = moLoggedInc > 0 ? moLoggedInc : plannedInc;

  const savA = moSavings(savDeposits, k, savContrib);
  const varTot = moVar(varSpend, k);
  const { budget: varBudget } = getVarBudget(inc, bills, savA, budgetSlider ?? 50);
  const varRem = varBudget - varTot;

  const paidCount = bills.filter((b) => !!pd[b.id]).length;
  const anyCatOver = cats.some((cat) => {
    const b = (catBudgets || {})[cat.id] || 0;
    return b > 0 && (vd[cat.id]?.reduce((s, x) => s + x.amt, 0) || 0) > b;
  });

  return (
    <div>
      {/* Month navigator */}
      <div className="card cp" style={{ marginBottom: 12 }}>
        <div className="mhn">
          <button className="mna" onClick={prevMo}>&#8249;</button>
          <div>
            <div className="mlb">{MOS[viewM]} {viewY}</div>
            <div className="mls">
              {isNowMo(viewY, viewM) ? 'Current month' : ''}
              {bills.length > 0 ? `${isNowMo(viewY, viewM) ? ' · ' : ''}${paidCount}/${bills.length} bills paid` : ''}
            </div>
          </div>
          <button className="mna" onClick={nextMo}>&#8250;</button>
        </div>
      </div>

      {/* Bills checklist */}
      <FixedBills
        activeBills={bills}
        paidBills={paidBills}
        cards={cards}
        now={today}
        viewY={viewY}
        viewM={viewM}
        k={k}
        updateState={updateState}
        showToast={showToast}
        triggerConfetti={triggerConfetti}
        bankBalance={state.bankBalance || 0}
        bankBalanceDate={state.bankBalanceDate}
      />

      {/* Variable spending */}
      <VariableSpending
        cats={cats}
        activeVd={Object.fromEntries(
          Object.entries(vd).map(([catId, items]) => [
            catId,
            (items || []).map((it, i) => ({ ...it, _moKey: k, _origIdx: i })),
          ])
        )}
        catBudgets={catBudgets}
        varSpend={varSpend}
        k={k}
        activeInc={inc}
        activeVarBudget={varBudget}
        activeVarTot={varTot}
        activeVarRem={varRem}
        anyCatOver={anyCatOver}
        varRem={varRem}
        budgetSlider={budgetSlider}
        customCats={customCats}
        disabledCats={disabledCats}
        state={state}
        updateState={updateState}
        showToast={showToast}
      />
    </div>
  );
}
